import React, { useRef } from 'react';
import { Tile, NameChip, MeterDock, AvatarCircle } from './CallTiles.styles';
import MicLevelMeter from './MicLevelMeter';

// The user's own avatar colour. Personas carry their own avatarColor from the
// meeting; the user has none, so this is fixed rather than derived, which keeps
// "you" the same colour every session.
const USER_AVATAR_COLOR = '#0784a7';

/**
 * The user's own tile in the Teams-style grid: their camera (or screen
 * capture), name chip, and the mic level meter docked bottom-right — which
 * also drives the green active-speaker ring by toggling a `speaking` class
 * directly on this tile's DOM node (see MicLevelMeter: no parent re-renders,
 * no second audio pipeline).
 *
 * Three states, and they are not the same thing:
 *   no stream      — pre-call. Renders `placeholder` (the "ready to start"
 *                    content, owned by the parent screen).
 *   camera off     — mid-call, deliberately switched off. Renders the same
 *                    avatar circle the persona tiles use.
 *   screen share   — sales presentation / investor pitch. The tile is the
 *                    shared screen, so camera-off never applies.
 *   camera on      — the video element.
 */
const UserTile = ({
  videoRef,
  hasStream,
  isCameraActive = true,
  isScreenShare = false,
  userName = '',
  placeholder = null,
  microphoneStream,
  isMicrophoneActive,
  recording,
}) => {
  const tileRef = useRef(null);

  // toggleCamera only flips track.enabled, so the stream object survives and
  // hasStream stays true. Without this the <video> keeps painting and the tile
  // just goes black, which reads as a broken feed rather than a camera that is
  // off on purpose.
  // Never in a screen-share mode: there the tile shows what the user is
  // sharing, not a camera, so an avatar would cover the presentation itself.
  const cameraOff = hasStream && !isCameraActive && !isScreenShare;
  const initial = (userName || '').trim().charAt(0).toUpperCase() || '?';

  return (
    <Tile ref={tileRef} data-testid="user-tile">
      {/* The video element mounts unconditionally — parent effects attach the
          stream to videoRef whenever it arrives; display toggles like the old
          layout did. It must remain a real <video> element. */}
      <video
        ref={videoRef}
        autoPlay
        muted={true} // Always mute to prevent audio feedback
        playsInline
        style={{ display: hasStream && !cameraOff ? 'block' : 'none' }}
      />

      {cameraOff && (
        <AvatarCircle $bg={USER_AVATAR_COLOR} $size={88} data-testid="user-avatar">
          {initial}
        </AvatarCircle>
      )}

      {!hasStream && placeholder}

      <NameChip><span>You</span></NameChip>

      <MeterDock>
        <MicLevelMeter
          microphoneStream={microphoneStream}
          isMicrophoneActive={isMicrophoneActive}
          recording={recording}
          speakingTargetRef={tileRef}
        />
      </MeterDock>
    </Tile>
  );
};

export default UserTile;
